'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Clock, PlayCircle, StopCircle, MapPin, RefreshCw, Wifi, WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface TimeClockInterfaceProps {
  staffId: string
  clinicId: string
  staffName: string
}

interface ActiveEntry {
  id: string
  clockInTime: string
  location?: string
}

interface TimeStats {
  today: number
  thisWeek: number
  thisMonth: number
}

interface PendingClockOut {
  entryId: string
  clockOutTime: string
}

// Polling interval for cross-browser sync (30 seconds)
const SYNC_INTERVAL = 30000
const PENDING_CLOCKOUT_KEY = 'pending_clock_out'

export default function TimeClockInterface({
  staffId,
  clinicId,
  staffName
}: TimeClockInterfaceProps) {
  const { t } = useTranslation('hr')
  const [activeEntry, setActiveEntry] = useState<ActiveEntry | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [elapsedTime, setElapsedTime] = useState('00:00:00')
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null)
  const [isOnline, setIsOnline] = useState(true)
  const [timeStats, setTimeStats] = useState<TimeStats>({ today: 0, thisWeek: 0, thisMonth: 0 })
  const [pendingClockOut, setPendingClockOut] = useState<PendingClockOut | null>(null)

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Calculate elapsed time
  useEffect(() => {
    if (activeEntry) {
      const timer = setInterval(() => {
        const start = new Date(activeEntry.clockInTime)
        const now = new Date()
        const diff = now.getTime() - start.getTime()
        
        const hours = Math.floor(diff / 3600000)
        const minutes = Math.floor((diff % 3600000) / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        
        setElapsedTime(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        )
      }, 1000)

      return () => clearInterval(timer)
    } else {
      setElapsedTime('00:00:00')
    }
  }, [activeEntry])

  // Load active entry and pending clock-out on mount
  useEffect(() => {
    checkActiveEntry()
    fetchTimeStats()
    loadPendingClockOut()
  }, [staffId, clinicId])

  // Load pending clock-out from localStorage
  const loadPendingClockOut = () => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(`${PENDING_CLOCKOUT_KEY}_${staffId}`)
      if (stored) {
        try {
          const pending = JSON.parse(stored) as PendingClockOut
          setPendingClockOut(pending)
        } catch (e) {
          console.error('Error loading pending clock-out:', e)
        }
      }
    }
  }

  // Save pending clock-out to localStorage
  const savePendingClockOut = (pending: PendingClockOut) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${PENDING_CLOCKOUT_KEY}_${staffId}`, JSON.stringify(pending))
      setPendingClockOut(pending)
    }
  }

  // Clear pending clock-out from localStorage
  const clearPendingClockOut = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(`${PENDING_CLOCKOUT_KEY}_${staffId}`)
      setPendingClockOut(null)
    }
  }

  // Sync pending clock-out when coming back online
  const syncPendingClockOut = async () => {
    if (!pendingClockOut) return false
    
    try {
      console.log('📤 Syncing pending clock-out...', pendingClockOut)
      
      const response = await fetch('/api/hr/time-tracking', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: pendingClockOut.entryId,
          clockOutTime: pendingClockOut.clockOutTime
        })
      })

      const data = await response.json()
      
      if (response.ok && data.success) {
        console.log('✅ Pending clock-out synced successfully')
        clearPendingClockOut()
        setActiveEntry(null)
        setElapsedTime('00:00:00')
        return true
      } else {
        console.error('Failed to sync pending clock-out:', data.error)
        return false
      }
    } catch (error) {
      console.error('Error syncing pending clock-out:', error)
      return false
    }
  }

  // Cross-browser synchronization: Poll for changes periodically
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (!loading) {
        syncFromServer()
      }
    }, SYNC_INTERVAL)

    return () => clearInterval(syncInterval)
  }, [staffId, clinicId, loading])

  // Sync when tab becomes visible (user switches back to this tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !loading) {
        console.log('🔄 Tab became visible, syncing from server...')
        syncFromServer()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [loading])

  // Sync when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      if (!loading) {
        console.log('🔄 Window focused, syncing from server...')
        syncFromServer()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [loading])

  // Track online/offline status and sync pending clock-out when back online
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true)
      console.log('🌐 Back online!')
      
      // First, sync any pending clock-out
      if (pendingClockOut) {
        await syncPendingClockOut()
      }
      
      // Then sync from server
      syncFromServer()
    }
    const handleOffline = () => {
      setIsOnline(false)
      console.log('📴 Gone offline - clock-out will be saved locally')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check initial online status
    setIsOnline(navigator.onLine)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [pendingClockOut])

  // Silent sync from server (no loading indicator for background sync)
  const syncFromServer = useCallback(async () => {
    if (syncing) return
    
    setSyncing(true)
    try {
      await checkActiveEntry(true)
      await fetchTimeStats()
      setLastSyncTime(new Date())
    } finally {
      setSyncing(false)
    }
  }, [staffId, clinicId, syncing])

  // Fetch time statistics
  const fetchTimeStats = async () => {
    try {
      const now = new Date()
      
      // Today's start
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      
      // This week's start (Monday)
      const dayOfWeek = now.getDay()
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
      const weekStart = new Date(now.setDate(diff))
      weekStart.setHours(0, 0, 0, 0)
      
      // This month's start
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      
      const response = await fetch(
        `/api/hr/time-tracking?staffId=${staffId}&clinicId=${clinicId}&startDate=${monthStart.toISOString()}`,
        { cache: 'no-store' }
      )
      const data = await response.json()
      
      if (data.timeEntries) {
        let todayHours = 0
        let weekHours = 0
        let monthHours = 0
        
        data.timeEntries.forEach((entry: any) => {
          const entryDate = new Date(entry.clock_in_time)
          const hours = entry.total_hours || 0
          
          // Add to month total
          monthHours += hours
          
          // Add to week total if in current week
          if (entryDate >= weekStart) {
            weekHours += hours
          }
          
          // Add to today total if today
          if (entryDate >= todayStart) {
            todayHours += hours
          }
        })
        
        setTimeStats({
          today: Math.round(todayHours * 10) / 10,
          thisWeek: Math.round(weekHours * 10) / 10,
          thisMonth: Math.round(monthHours * 10) / 10
        })
      }
    } catch (error) {
      console.error('Error fetching time stats:', error)
    }
  }

  const checkActiveEntry = async (silent: boolean = false) => {
    try {
      if (!silent) {
        console.log('Checking for active entry...', { staffId, clinicId })
      }
      
      // Check for ANY active entry (no time limit) - user might have forgotten to clock out days ago
      // The activeOnly=true parameter will find entries without clock_out_time
      // cache: 'no-store' is critical - a cached response here makes the UI
      // revert to a stale clocked-in/out state after the user clicks the button
      const response = await fetch(
        `/api/hr/time-tracking?staffId=${staffId}&clinicId=${clinicId}&activeOnly=true`,
        { cache: 'no-store' }
      )
      const data = await response.json()
      
      if (!silent) {
        console.log('Active entry check response:', data)
        console.log('Number of entries found:', data.timeEntries?.length || 0)
      }
      
      if (data.timeEntries && data.timeEntries.length > 0) {
        // Find the first entry without a clock out time from the last 24 hours
        const foundActiveEntry = data.timeEntries.find((entry: any) => {
          if (!silent) {
            console.log('Checking entry:', {
              id: entry.id,
              clock_in: entry.clock_in_time,
              clock_out: entry.clock_out_time,
              has_no_clock_out: !entry.clock_out_time
            })
          }
          return !entry.clock_out_time
        })
        
        if (foundActiveEntry) {
          if (!silent) console.log('✅ Found active entry:', foundActiveEntry.id)
          
          // Only update if different from current (prevents unnecessary re-renders)
          setActiveEntry(prev => {
            if (!prev || prev.id !== foundActiveEntry.id) {
              return {
                id: foundActiveEntry.id,
                clockInTime: foundActiveEntry.clock_in_time,
                location: foundActiveEntry.clock_in_location
              }
            }
            return prev
          })
        } else {
          if (!silent) console.log('❌ No active entry (all have clock_out_time)')
          setActiveEntry(null)
        }
      } else {
        if (!silent) console.log('❌ No entries found in last 24 hours')
        setActiveEntry(null)
      }
    } catch (error) {
      console.error('Error checking active entry:', error)
    }
  }

  const handleClockIn = async () => {
    setLoading(true)
    try {
      console.log('Clock in started', { staffId, clinicId })
      
      // Get user location (optional)
      let location = 'Unknown'
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          })
          location = `${position.coords.latitude},${position.coords.longitude}`
        } catch (err) {
          console.log('Location not available:', err)
        }
      }

      // Get IP address with timeout
      let ipAddress = 'Unknown'
      try {
        const ipResponse = await Promise.race([
          fetch('https://api.ipify.org?format=json'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
        ]) as Response
        const ipData = await ipResponse.json()
        ipAddress = ipData.ip
      } catch (err) {
        console.log('IP address not available:', err)
      }

      const requestBody = {
        staffId,
        clinicId,
        clockInTime: new Date().toISOString(),
        location,
        ipAddress
      }
      
      console.log('Sending clock in request:', requestBody)

      const response = await fetch('/api/hr/time-tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()
      console.log('Clock in response:', data)
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to clock in')
      }
      
      if (data.success && data.timeEntry) {
        setActiveEntry({
          id: data.timeEntry.id,
          clockInTime: data.timeEntry.clock_in_time,
          location: data.timeEntry.clock_in_location
        })
        alert(t('timeClock.clockInSuccess'))
        // Refresh active entry to ensure it's properly loaded
        await checkActiveEntry()
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Clock in error:', error)
      alert(`Failed to clock in: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleClockOut = async () => {
    if (!activeEntry) return

    const clockOutTime = new Date().toISOString()
    setLoading(true)
    
    // If offline, save clock-out locally and show it will sync later
    if (!isOnline) {
      console.log('📴 Offline - saving clock-out locally')
      savePendingClockOut({
        entryId: activeEntry.id,
        clockOutTime: clockOutTime
      })
      
      // Update UI immediately - show as clocked out with pending sync
      setActiveEntry(null)
      setElapsedTime('00:00:00')
      setLoading(false)
      alert(t('timeClock.clockOutOfflineSuccess'))
      return
    }

    try {
      console.log('Clock out started', { entryId: activeEntry.id })
      
      const response = await fetch('/api/hr/time-tracking', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: activeEntry.id,
          clockOutTime: clockOutTime
        })
      })

      const data = await response.json()
      console.log('Clock out response:', data)
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to clock out')
      }
      
      if (data.success) {
        setActiveEntry(null)
        setElapsedTime('00:00:00')
        alert(t('timeClock.clockOutSuccess'))
        // Refresh stats
        await fetchTimeStats()
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Clock out error:', error)
      
      // If request failed (maybe network issue), save locally
      console.log('📴 Request failed - saving clock-out locally')
      savePendingClockOut({
        entryId: activeEntry.id,
        clockOutTime: clockOutTime
      })
      
      setActiveEntry(null)
      setElapsedTime('00:00:00')
      alert(t('timeClock.clockOutOfflineFallback'))
    } finally {
      setLoading(false)
    }
  }

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setLoading(true)
    try {
      await syncFromServer()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-6 w-6" />
            {t('timeClock.title')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Sync Status Indicator */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {isOnline ? (
                <Wifi className="h-3 w-3 text-green-500" />
              ) : (
                <WifiOff className="h-3 w-3 text-red-500" />
              )}
              {syncing && (
                <RefreshCw className="h-3 w-3 animate-spin text-blue-500" />
              )}
              {lastSyncTime && !syncing && (
                <span className="text-[10px]">
                  {t('timeClock.synced')}: {lastSyncTime.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            {/* Manual Refresh Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleManualRefresh}
              disabled={loading || syncing}
              className="h-8 w-8 p-0"
              title={t('timeClock.refreshFromServer')}
            >
              <RefreshCw className={`h-4 w-4 ${loading || syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Time Display */}
        <div className="text-center space-y-2">
          <div className="text-5xl font-bold text-primary">
            {currentTime.toLocaleTimeString('sv-SE', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
          <div className="text-sm text-muted-foreground">
            {currentTime.toLocaleDateString('sv-SE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>

        {/* Staff Info */}
        <div className="flex items-center justify-center gap-2 text-lg">
          <Badge variant="outline">{staffName}</Badge>
        </div>

        {/* Active Entry Status */}
        {activeEntry && (() => {
          const clockInDate = new Date(activeEntry.clockInTime)
          const now = new Date()
          const hoursElapsed = (now.getTime() - clockInDate.getTime()) / (1000 * 60 * 60)
          const isOvernight = hoursElapsed > 12
          const daysElapsed = Math.floor(hoursElapsed / 24)
          const isMultipleDays = daysElapsed >= 1
          
          return (
            <div className={`${isOvernight ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'} border rounded-lg p-4 space-y-3`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t('timeClock.clockedIn')}</span>
                <Badge variant="default" className={isOvernight ? "bg-orange-600" : "bg-green-600"}>
                  {isOvernight ? t('timeClock.longSession') : t('timeClock.active')}
                </Badge>
              </div>
              
              {/* Warning for long sessions */}
              {isOvernight && (
                <div className={`${isMultipleDays ? 'bg-red-100 border-red-300 text-red-800' : 'bg-orange-100 border-orange-300 text-orange-800'} border rounded-lg p-3 text-sm`}>
                  <p className="font-medium">
                    {isMultipleDays 
                      ? `⚠️ ${t('timeClock.clockedInDaysHours', { days: daysElapsed, hours: Math.floor(hoursElapsed % 24) })}`
                      : `⚠️ ${t('timeClock.clockedInHours', { hours: Math.floor(hoursElapsed) })}`
                    }
                  </p>
                  <p className="text-xs mt-1">
                    {t('timeClock.forgotClockOut')}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">{t('timeClock.clockInTime')}</div>
                  <div className="font-medium">
                    {isMultipleDays && (
                      <span className="text-xs text-muted-foreground block">
                        {clockInDate.toLocaleDateString('sv-SE', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    {clockInDate.toLocaleTimeString('sv-SE')}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('timeClock.workingTime')}</div>
                  <div className={`font-mono text-lg font-bold ${isOvernight ? 'text-orange-600' : 'text-green-600'}`}>
                    {elapsedTime}
                  </div>
                </div>
              </div>

              {activeEntry.location && activeEntry.location !== 'Unknown' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{t('timeClock.locationRegistered')}</span>
                </div>
              )}
            </div>
          )
        })()}

        {/* Not clocked in status */}
        {!activeEntry && (
          <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4 text-center">
            <p className="text-muted-foreground">{t('timeClock.notClockedIn')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('timeClock.clickClockIn')}
            </p>
          </div>
        )}

        {/* Pending Clock-Out Status */}
        {pendingClockOut && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                {t('timeClock.waitingSync')}
              </span>
              <Badge variant="outline" className="border-orange-400 text-orange-700">
                {t('timeClock.pending')}
              </Badge>
            </div>
            <p className="text-xs text-orange-700 dark:text-orange-300">
              {t('timeClock.clockedOutOffline', { time: new Date(pendingClockOut.clockOutTime).toLocaleTimeString('sv-SE') })}
            </p>
            {isOnline && (
              <Button
                size="sm"
                variant="outline"
                onClick={syncPendingClockOut}
                disabled={loading}
                className="mt-2"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                {t('timeClock.syncNow')}
              </Button>
            )}
          </div>
        )}

        {/* Clock In/Out Buttons */}
        <div className="flex gap-4">
          {!activeEntry ? (
            <Button
              onClick={handleClockIn}
              disabled={loading || !isOnline || !!pendingClockOut}
              className="flex-1 h-16 text-lg bg-green-600 hover:bg-green-700"
              size="lg"
            >
              <PlayCircle className="h-6 w-6 mr-2" />
              {t('timeClock.clockIn')}
            </Button>
          ) : (
            <Button
              onClick={handleClockOut}
              disabled={loading}
              variant="destructive"
              className="flex-1 h-16 text-lg"
              size="lg"
            >
              <StopCircle className="h-6 w-6 mr-2" />
              {t('timeClock.clockOut')}
            </Button>
          )}
        </div>

        {/* Offline Info */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <div className="flex items-center gap-2">
              <WifiOff className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">{t('timeClock.offline')}</p>
                <p className="text-xs mt-0.5">
                  {activeEntry 
                    ? t('timeClock.offlineCanClockOut')
                    : t('timeClock.offlineMustBeOnline')
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cross-browser sync info */}
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
          <p className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            <span>{t('timeClock.syncInfo')}</span>
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold">{timeStats.today}h</div>
            <div className="text-sm text-muted-foreground">{t('timeClock.today')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{timeStats.thisWeek}h</div>
            <div className="text-sm text-muted-foreground">{t('timeClock.thisWeek')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{timeStats.thisMonth}h</div>
            <div className="text-sm text-muted-foreground">{t('timeClock.thisMonth')}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

